<template>
  <div class="p-4" dir="rtl">
    <h1 class="text-2xl font-bold mb-1">حضور وانصراف المعلمين (إقرأ)</h1>
    <p class="text-sm text-gray-500 mb-4">
      صف لكل معلم (حسب الجدول) — اضغط على الصف لعرض كل فتراته.
      يُحتسب الحضور من دخول المعلم الفعلي لغرفة الجلسة، مع وقت الحضور ووقت الانصراف ومدة البقاء.
      <span class="text-gray-400">
        الفترات التي لا يوجد بها طلاب مسجلون لا تُحتسب على المعلم. ويُعدّ متأخراً إذا دخل بعد {{ lateGraceMinutes }} دقائق من بداية الجلسة.
      </span>
    </p>

    <div class="flex flex-wrap gap-3 mb-4 items-end bg-gray-50 p-3 rounded border">
      <div>
        <label class="block mb-1 text-sm">من تاريخ</label>
        <input v-model="filters.dateFrom" type="date" class="border px-3 py-2 rounded" />
      </div>
      <div>
        <label class="block mb-1 text-sm">إلى تاريخ</label>
        <input v-model="filters.dateTo" type="date" class="border px-3 py-2 rounded" />
      </div>
      <div>
        <label class="block mb-1 text-sm">الحالة</label>
        <select v-model="filters.status" class="border px-3 py-2 rounded w-44">
          <option value="">الكل</option>
          <option value="present">حضر</option>
          <option value="late">متأخر</option>
          <option value="absent">لم يحضر</option>
          <option value="no_students">لا يوجد طلاب</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">الجدول</label>
        <select v-model="filters.scheduleId" @change="handleScheduleChange" class="border px-3 py-2 rounded w-56">
          <option value="">كل الجداول</option>
          <option v-for="s in scheduleOptions" :key="s.id" :value="s.id">{{ s.title }}</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">المعلم</label>
        <select v-model="filters.teacherId" class="border px-3 py-2 rounded w-48" :disabled="!filters.scheduleId">
          <option value="">كل المعلمين</option>
          <option v-for="t in teacherOptions" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>
      </div>
      <div class="flex-1 min-w-[200px]">
        <label class="block mb-1 text-sm">بحث (المعلم / الجدول / الفترة)</label>
        <input v-model="filters.search" type="text" class="border px-3 py-2 rounded w-full" placeholder="نص للبحث" />
      </div>
      <button @click="search" class="bg-blue-600 text-white px-4 py-2 rounded">بحث</button>
      <button @click="resetFilters" class="bg-gray-200 px-4 py-2 rounded">إزالة الفلاتر</button>
      <button @click="load" class="bg-white border px-4 py-2 rounded" title="تحديث">↻</button>
      <button
        @click="exportExcel"
        :disabled="exporting || totalElements === 0"
        class="bg-green-700 text-white px-4 py-2 rounded disabled:opacity-50">
        {{ exporting ? 'جاري التصدير...' : 'تصدير Excel' }}
      </button>
    </div>

    <div v-if="message" :class="messageClass" class="mb-3 px-4 py-2 rounded border text-sm">{{ message }}</div>

    <div v-if="summary" class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
      <div class="border rounded p-3 bg-white">
        <div class="text-xs text-gray-500">إجمالي الفترات</div>
        <div class="text-xl font-bold">{{ summary.total }}</div>
      </div>
      <div class="border rounded p-3 bg-green-50 border-green-200">
        <div class="text-xs text-green-700">حضر</div>
        <div class="text-xl font-bold text-green-800">{{ summary.present }}</div>
      </div>
      <div class="border rounded p-3 bg-amber-50 border-amber-200">
        <div class="text-xs text-amber-700">متأخر</div>
        <div class="text-xl font-bold text-amber-800">{{ summary.late }}</div>
      </div>
      <div class="border rounded p-3 bg-red-50 border-red-200">
        <div class="text-xs text-red-700">لم يحضر</div>
        <div class="text-xl font-bold text-red-800">{{ summary.absent }}</div>
      </div>
      <div class="border rounded p-3 bg-gray-50 border-gray-200">
        <div class="text-xs text-gray-600">لا يوجد طلاب</div>
        <div class="text-xl font-bold text-gray-700">{{ summary.noStudents }}</div>
      </div>
    </div>

    <div v-if="summary" class="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-600">
      <div v-if="summary.attendanceRate !== null">
        نسبة الحضور العامة: <span class="font-bold text-customHafadhColor">{{ summary.attendanceRate }}%</span>
        <span class="text-gray-400">(الفترات بلا طلاب غير محسوبة)</span>
      </div>
      <div>عدد المعلمين: <span class="font-bold">{{ teacherCount }}</span></div>
    </div>

    <div v-if="loading" class="py-6 text-center text-gray-500">جاري التحميل...</div>
    <div v-else>
      <div v-if="groups.length === 0" class="py-6 text-gray-500 text-center">لا توجد سجلات مطابقة</div>

      <div v-else>
        <div class="flex flex-wrap items-center gap-2 mb-2">
          <button @click="expandAll" class="text-xs border px-3 py-1 rounded bg-white">توسيع الكل</button>
          <button @click="collapseAll" class="text-xs border px-3 py-1 rounded bg-white">طيّ الكل</button>
          <div class="flex-1"></div>
          <label class="text-xs text-gray-500">ترتيب المعلمين</label>
          <select v-model="sort" @change="reload" class="border px-2 py-1 rounded text-sm">
            <option value="name">الاسم أبجدياً</option>
            <option value="absent">الأكثر تغيّباً</option>
            <option value="rate">الأقل نسبة حضور</option>
          </select>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full border text-sm bg-white">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="p-2 border w-8"></th>
                <th class="p-2 border">المعلم</th>
                <th class="p-2 border">الجدول</th>
                <th class="p-2 border">التوزيع</th>
                <th class="p-2 border whitespace-nowrap">نسبة الحضور</th>
                <th class="p-2 border whitespace-nowrap">آخر حضور</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="g in groups" :key="g.groupKey">
                <tr
                  @click="toggle(g.groupKey)"
                  class="cursor-pointer hover:bg-blue-50"
                  :class="isExpanded(g.groupKey) ? 'bg-blue-50' : ''">
                  <td class="p-2 border text-center text-gray-400">{{ isExpanded(g.groupKey) ? '▾' : '◂' }}</td>
                  <td class="p-2 border text-xs">
                    <div class="font-semibold">{{ g.teacherName || '—' }}</div>
                    <div class="text-gray-500">#{{ g.teacherId }} — {{ g.counts.total }} فترة</div>
                  </td>
                  <td class="p-2 border text-xs">{{ g.scheduleTitle || '—' }}</td>
                  <td class="p-2 border text-xs">
                    <span v-for="p in countPills(g)" :key="p.key" :class="p.cls" class="inline-block px-2 py-0.5 rounded ml-1 mb-1">
                      {{ p.label }} {{ p.n }}
                    </span>
                  </td>
                  <td class="p-2 border text-xs whitespace-nowrap">
                    <span v-if="g.attendanceRate !== null" :class="rateClass(g.attendanceRate)" class="font-bold">
                      {{ g.attendanceRate }}%
                    </span>
                    <span v-else class="text-gray-400" title="لا توجد فترات محسوبة في هذه المدة">—</span>
                  </td>
                  <td class="p-2 border text-xs whitespace-nowrap">
                    <span v-if="g.lastAttendedAt">{{ formatDay(g.lastAttendedAt) }}</span>
                    <span v-else class="text-red-500">لم يحضر</span>
                  </td>
                </tr>

                <tr v-if="isExpanded(g.groupKey)" :key="g.groupKey + '-details'">
                  <td colspan="6" class="border bg-gray-50 p-3">
                    <table class="w-full border text-xs bg-white">
                      <thead class="bg-gray-100">
                        <tr>
                          <th class="p-2 border whitespace-nowrap">التاريخ</th>
                          <th class="p-2 border">الفترة</th>
                          <th class="p-2 border whitespace-nowrap">عدد الطلاب</th>
                          <th class="p-2 border">الحالة</th>
                          <th class="p-2 border whitespace-nowrap">بداية الجلسة</th>
                          <th class="p-2 border whitespace-nowrap">وقت الحضور</th>
                          <th class="p-2 border whitespace-nowrap">وقت الانصراف</th>
                          <th class="p-2 border whitespace-nowrap">المدة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(s, i) in g.sessions" :key="g.groupKey + '-' + i" :class="rowClass(s.status)">
                          <td class="p-2 border whitespace-nowrap">
                            <div>{{ formatDay(s.occurrenceDate) }}</div>
                            <div class="text-gray-500">{{ weekdayLabel(s.weekday) }}</div>
                          </td>
                          <td class="p-2 border">{{ s.slotName || '—' }}</td>
                          <td class="p-2 border text-center">{{ s.studentCount }}</td>
                          <td class="p-2 border whitespace-nowrap">
                            <span :class="statusBadgeClass(s.status)">{{ statusLabel(s.status) }}</span>
                          </td>
                          <td class="p-2 border whitespace-nowrap">{{ formatTime(s.scheduledStartAt) }}</td>
                          <td class="p-2 border whitespace-nowrap">{{ formatTime(s.joinedAt) }}</td>
                          <td class="p-2 border whitespace-nowrap">
                            <template v-if="s.leftAt">
                              <div>{{ formatTime(s.leftAt) }}</div>
                              <div v-if="s.isExitApproximate" class="text-gray-400" title="لم يُسجَّل انصراف نظيف — هذا آخر ظهور للمعلم في الجلسة">
                                تقريبي
                              </div>
                            </template>
                            <span v-else-if="s.joinedAt" class="text-gray-400">لم يسجل انصراف</span>
                            <span v-else class="text-gray-400">—</span>
                          </td>
                          <td class="p-2 border whitespace-nowrap">
                            {{ s.durationMinutes !== null ? s.durationMinutes + ' د' : '—' }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-center justify-between mt-4">
        <div class="text-sm text-gray-600">
          صفحة {{ currentPage + 1 }} من {{ totalPages || 1 }} — إجمالي {{ totalElements }} معلم
        </div>
        <div class="flex items-center gap-2">
          <button :disabled="currentPage === 0" @click="prev" class="px-3 py-1 border rounded disabled:opacity-50">السابق</button>
          <button :disabled="currentPage >= totalPages - 1" @click="next" class="px-3 py-1 border rounded disabled:opacity-50">التالي</button>
          <select v-model.number="pageSize" @change="reload" class="border px-2 py-1 rounded">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <select v-model="direction" @change="reload" class="border px-2 py-1 rounded" title="ترتيب الفترات حسب بداية الجلسة">
            <option value="asc">بداية الجلسة: الأقدم أولاً</option>
            <option value="desc">بداية الجلسة: الأحدث أولاً</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import url from '@/url';
import { listIqraSchedules, listIqraSlotTeachers } from '@/services/iqraSchedules';

// The backend resolves dates in APP_TIMEZONE (Asia/Riyadh). toISOString() would
// send the UTC date and slip a day either side of local midnight.
const TENANT_TZ = process.env.VUE_APP_TENANT_TIMEZONE || 'Asia/Riyadh';
const tenantToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: TENANT_TZ }).format(new Date());

const STATUS_LABELS = {
  present: 'حضر',
  late: 'متأخر',
  absent: 'لم يحضر',
  no_students: 'لا يوجد طلاب',
};

const emptyFilters = () => ({
  dateFrom: tenantToday(),
  dateTo: tenantToday(),
  status: '',
  scheduleId: '',
  teacherId: '',
  search: '',
});

export default {
  name: 'IqraTeacherAttendanceReport',
  data() {
    return {
      groups: [],
      summary: null,
      teacherCount: 0,
      lateGraceMinutes: 5,
      loading: false,
      exporting: false,
      message: '',
      messageType: 'info',
      currentPage: 0,
      pageSize: 25,
      totalPages: 0,
      totalElements: 0,
      // Chronological through the day — matches "ordered by session start".
      direction: 'asc',
      sort: 'name',
      expanded: {},
      scheduleOptions: [],
      teacherOptions: [],
      filters: emptyFilters(),
    };
  },
  computed: {
    messageClass() {
      if (this.messageType === 'error') return 'bg-red-50 border-red-200 text-red-700';
      if (this.messageType === 'success') return 'bg-green-50 border-green-200 text-green-700';
      return 'bg-blue-50 border-blue-200 text-blue-700';
    },
  },
  mounted() {
    this.loadScheduleOptions();
    this.load();
  },
  methods: {
    buildParams(overrides = {}) {
      const f = this.filters;
      return {
        page: this.currentPage,
        size: this.pageSize,
        direction: this.direction,
        sort: this.sort,
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
        status: f.status || undefined,
        scheduleId: f.scheduleId || undefined,
        teacherId: f.teacherId || undefined,
        search: f.search?.trim() || undefined,
        ...overrides,
      };
    },
    async fetch(params) {
      const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.iqra_teacher_attendance_report, {
        params,
        headers: { 'X-Authorization': localStorage.getItem('token') },
      });
      return res.data?.result?.data || res.data?.data;
    },
    async load() {
      if (!this.filters.dateFrom || !this.filters.dateTo) {
        this.setMessage('يرجى تحديد الفترة الزمنية', 'error');
        return;
      }
      this.loading = true;
      try {
        const data = await this.fetch(this.buildParams());
        this.groups = data?.content || [];
        this.summary = data?.summary || null;
        this.teacherCount = data?.teacherCount || 0;
        this.lateGraceMinutes = data?.lateGraceMinutes ?? this.lateGraceMinutes;
        this.totalElements = data?.totalElements || 0;
        this.totalPages = data?.totalPages || 0;
        this.currentPage = data?.currentPage || 0;
        this.pageSize = data?.pageSize || this.pageSize;
        this.expanded = {};
      } catch (e) {
        console.error('Failed loading teacher attendance report', e);
        this.groups = [];
        this.summary = null;
        this.setMessage(e?.response?.data?.result?.mesAr || 'تعذر تحميل البيانات', 'error');
      } finally {
        this.loading = false;
      }
    },
    async loadScheduleOptions() {
      try {
        const result = await listIqraSchedules({ is_active: true, page: 1, size: 100 });
        const data = result?.data ?? [];
        this.scheduleOptions = (Array.isArray(data) ? data : []).map((s) => ({
          id: String(s.id),
          title: s.title || `جدول #${s.id}`,
        }));
      } catch (err) {
        console.error('Error loading schedules:', err);
      }
    },
    async loadTeacherOptions() {
      this.filters.teacherId = '';
      this.teacherOptions = [];
      if (!this.filters.scheduleId) return;
      try {
        const rows = await listIqraSlotTeachers({ schedule_id: this.filters.scheduleId, is_active: true });
        const list = Array.isArray(rows) ? rows : (rows?.data ?? []);
        // A schedule has many slot-teacher rows; collapse to a distinct teacher list.
        const seen = new Map();
        list.forEach((row) => {
          if (row.teacher_id == null) return;
          const id = String(row.teacher_id);
          if (!seen.has(id)) seen.set(id, { id, name: row.teacher_name || `معلم #${id}` });
        });
        this.teacherOptions = Array.from(seen.values());
      } catch (err) {
        console.error('Error loading teachers for schedule:', err);
        this.teacherOptions = [];
      }
    },
    async handleScheduleChange() {
      await this.loadTeacherOptions();
    },
    async exportExcel() {
      this.exporting = true;
      try {
        // flat=1 returns one row per teacher-slot-day — the shape this file wants —
        // instead of the nested groups the table renders.
        const data = await this.fetch(this.buildParams({ page: 0, size: 5000, flat: 1 }));
        const list = data?.content || [];
        if (!list.length) {
          this.setMessage('لا توجد بيانات للتصدير', 'error');
          return;
        }
        const rows = list.map((r) => ({
          'التاريخ': r.occurrenceDate || '',
          'اليوم': this.weekdayLabel(r.weekday),
          'المعلم': r.teacherName || '',
          'الجدول': r.scheduleTitle || '',
          'الفترة': r.slotName || '',
          'عدد الطلاب': r.studentCount ?? '',
          'الحالة': this.statusLabel(r.status),
          'بداية الجلسة': this.formatTime(r.scheduledStartAt),
          'وقت الحضور': this.formatTime(r.joinedAt),
          'وقت الانصراف': this.formatTime(r.leftAt),
          'الانصراف تقريبي': r.isExitApproximate ? 'نعم' : '',
          'المدة (دقيقة)': r.durationMinutes ?? '',
        }));
        const headers = Object.keys(rows[0]);
        const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
        ws['!cols'] = headers.map(() => ({ wch: 18 }));
        const wb = XLSX.utils.book_new();
        // RTL must be set on the workbook views; a worksheet-level '!views' is ignored.
        wb.Workbook = { Views: [{ RTL: true }] };
        XLSX.utils.book_append_sheet(wb, ws, 'حضور المعلمين');
        const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(
          new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `حضور_المعلمين_${this.filters.dateFrom}_${this.filters.dateTo}.xlsx`
        );
        this.setMessage(`تم تصدير ${list.length} سجلاً`, 'success');
      } catch (e) {
        console.error('Export failed', e);
        this.setMessage(e?.response?.data?.result?.mesAr || 'فشل التصدير', 'error');
      } finally {
        this.exporting = false;
      }
    },
    isExpanded(key) { return !!this.expanded[key]; },
    toggle(key) { this.expanded = { ...this.expanded, [key]: !this.expanded[key] }; },
    expandAll() {
      const next = {};
      this.groups.forEach((g) => { next[g.groupKey] = true; });
      this.expanded = next;
    },
    collapseAll() { this.expanded = {}; },
    countPills(g) {
      const defs = [
        { key: 'present', label: 'حضر', n: g.counts.present, cls: 'bg-green-100 text-green-700' },
        { key: 'late', label: 'متأخر', n: g.counts.late, cls: 'bg-amber-100 text-amber-700' },
        { key: 'absent', label: 'لم يحضر', n: g.counts.absent, cls: 'bg-red-100 text-red-700' },
        { key: 'noStudents', label: 'بلا طلاب', n: g.counts.noStudents, cls: 'bg-gray-100 text-gray-600' },
      ];
      return defs.filter((d) => d.n > 0);
    },
    rateClass(rate) {
      if (rate >= 80) return 'text-green-700';
      if (rate >= 50) return 'text-amber-600';
      return 'text-red-600';
    },
    setMessage(text, type) {
      this.message = text;
      this.messageType = type || 'info';
      setTimeout(() => { this.message = ''; }, 6000);
    },
    search() { this.currentPage = 0; this.load(); },
    resetFilters() {
      this.filters = emptyFilters();
      this.teacherOptions = [];
      this.sort = 'name';
      this.currentPage = 0;
      this.load();
    },
    prev() { if (this.currentPage > 0) { this.currentPage--; this.load(); } },
    next() { if (this.currentPage < this.totalPages - 1) { this.currentPage++; this.load(); } },
    reload() { this.currentPage = 0; this.load(); },
    formatDay(d) {
      if (!d) return '—';
      try { return new Date(d).toLocaleDateString('ar-SA'); } catch { return d; }
    },
    formatTime(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
      } catch { return iso; }
    },
    weekdayLabel(w) {
      // Iqra mapping: 1..6 => Mon..Sat, 7 => Sun
      return {
        1: 'الاثنين', 2: 'الثلاثاء', 3: 'الأربعاء', 4: 'الخميس',
        5: 'الجمعة', 6: 'السبت', 7: 'الأحد',
      }[w] || '';
    },
    statusLabel(s) { return STATUS_LABELS[s] || s; },
    statusBadgeClass(s) {
      const base = 'px-2 py-1 rounded text-white text-xs whitespace-nowrap';
      if (s === 'present') return base + ' bg-green-600';
      if (s === 'late') return base + ' bg-amber-500';
      if (s === 'absent') return base + ' bg-red-600';
      if (s === 'no_students') return base + ' bg-gray-400';
      return base + ' bg-gray-600';
    },
    rowClass(s) {
      if (s === 'absent') return 'bg-red-50';
      if (s === 'no_students') return 'bg-gray-50 text-gray-500';
      return '';
    },
  },
};
</script>
